/**
 * ORA - UNIFIED AI AGENT
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * 
 * ============================================================================
 * UNIFIED AGENT INTERFACE - Mobile-First PWA Architecture
 * ============================================================================
 * 
 * This component replaces AIAgentModal and AIAgentSidePanel with a single,
 * modular interface optimized for:
 * - Progressive Web Apps (PWA) - Overlay mode by default
 * - Chrome/Edge Extensions - Shared backend
 * - Multi-agent collaboration via AgentConnectionsManager
 * 
 * USER FLOW:
 * 1. Initial Screen: SME carousel (9 agents) + SELECT button
 * 2. Agent Selected: Video moves to top-right, auto-plays with sound
 * 3. Chat Active: Full chat interface + small video corner + voice input
 * 4. Video Expanded: Full-screen video (tap to control/minimize)
 * 
 * DESIGN RULES:
 * - Agent interface ALWAYS displays in LIGHT MODE
 * - Text is pure BLACK (#000000) for readability
 * - Web links: underline + dark blue (text-blue-800)
 * - Bottom 6% gray (#F3F4F6) for SME carousel
 * - Video auto-plays with sound ON when selected
 * - Hamburger menu: bottom-right corner
 * - Mic icon: inside "Ask?" input field (right corner)
 * 
 * KEYWORD VIDEO TRIGGERS (~25 keywords):
 * - Leadership, Innovation, Agile, USMC, Security, Governance, etc.
 * - See topicVideoMap for full list
 * ============================================================================
 */

import { useState, useRef, useEffect } from "react";
import { 
  X, 
  Mic, 
  Menu, 
  ThumbsUp, 
  ThumbsDown, 
  Minimize2,
  ChevronLeft,
  ChevronRight,
  Brain,
  Users,
  TrendingUp,
  Scale,
  DollarSign,
  Database,
  Cog,
  Heart,
  Shield,
  Wrench,
  BarChart3,
  Settings,
  Trash2,
  CheckCircle2,
  BookOpen,
  Layers,
  Radio
} from "lucide-react";
import { useNavigation } from "@/contexts/NavigationContext";
import { useRecentActivity } from "@/hooks/useRecentActivity";
import { RecentActivity } from "./RecentActivity";
import { trackVideoView } from "@/utils/analyticsTracking";
import { ReportIssueModal } from "./ReportIssueModal";
import { PricingPage } from "./PricingPage";
import { AgentConnectionsManager } from "./AgentConnectionsManager";
import { AgentCollaborationIndicator, type CollaboratingAgent } from "./AgentCollaborationIndicator";
import { AGENT_SYSTEM_PROMPT, DEFAULT_KNOWLEDGE_BASE } from "@/app/data/defaultKnowledgeBase";
import { searchKnowledgeBase } from "@/app/utils/knowledgeBaseSearch";
import { ExtensionBridge, useExtension } from "./ExtensionBridge";

interface UnifiedAIAgentProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: "pwa" | "extension" | "modal" | "embedded" | "desktop";
  currentTier?: 'Free' | 'Pro' | 'Team' | 'Enterprise' | 'Custom';
  theme?: "light" | "dark";
  onThemeToggle?: () => void;
  showNavigation?: boolean;
  onUpgradeClick?: () => void;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  id?: string;
  timestamp?: number;
  feedback?: "thumbs_up" | "thumbs_down" | null;
  collaboratingAgents?: CollaboratingAgent[];
  isMultiAgent?: boolean;
}

interface SMEAgent {
  id: string;
  name: string;
  avatar: string;
  videoUrl: string;
  thumbnailUrl?: string; // Optional thumbnail for carousel display
  specialty: string;
  icon: any;
  description: string;
}

// SME Agent Definitions (10 specialized agents)
const SME_AGENTS: SMEAgent[] = [
  {
    id: "ai-engineer",
    name: "Transformational AI Engineer",
    avatar: "🛠️",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/MIT-Arnold.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.Transformation.png",
    specialty: "Information Architecture & Technology",
    icon: Wrench,
    description: "Expert in system architecture, technical strategy, and engineering leadership"
  },
  {
    id: "training-workforce",
    name: "Training & Workforce Development",
    avatar: "📚",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/Process-Citrus.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.Training.png",
    specialty: "Learning & Development",
    icon: Users,
    description: "Hi! I'm the Training & Workforce Development SME, your Learning & Development expert. Specialized in training programs, skill development, workforce readiness, process improvement, additive processing, and organizational learning strategies. Are you ready to work together?"
  },
  {
    id: "communications",
    name: "Marketing & Outreach",
    avatar: "📢",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.Outreach.png",
    specialty: "Marketing & Growth",
    icon: TrendingUp,
    description: "Hi! I'm your Marketing & Growth expert. Expert in brand strategy, growth marketing, and customer acquisition. Are you ready to collaborate?"
  },
  {
    id: "legal",
    name: "Policy & Governance Advisor",
    avatar: "⚖️",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/PrositionsNono/Sage-Leaders.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.Compliance.png",
    specialty: "Corporate Law & Compliance",
    icon: Scale,
    description: "Hi! I'm a Policy & Governance Advisor. Specialized in corporate governance, contracts, and regulatory compliance. Are you ready to accelerate innovation?"
  },
  {
    id: "product-owner",
    name: "Product Owner - AI Leader",
    avatar: "🎯",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/AI_Literacy_GovBriefpart2.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.AI-Owner.png",
    specialty: "Strategic Leadership",
    icon: Brain,
    description: "Hi! I'm an AI Product Owner - your Strategic AI Leadership expert. Designed for strategic AI implementation and measurable organizational transformation. The ORA framework can help you deliver 10% greater returns in a fraction of the time! Are you ready to innovate with agility and automated efficiency?"
  },
  {
    id: "finance",
    name: "Chief Financial Officer",
    avatar: "💰",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/CWO-John2026.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.CFO.png",
    specialty: "Financial Strategies",
    icon: DollarSign,
    description: "Hi! I'm Chief Financial Officer, your Financial Strategies expert. How can I help?"
  },
  {
    id: "data-scientist",
    name: "Data Scientist",
    avatar: "📊",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/Validation1.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.Data.png",
    specialty: "Analytics & Insights",
    icon: Database,
    description: "Hi! I'm a Data Scientist, your Analytics & Insights expert. Specialized in data analysis, ML/AI implementation, and metrics strategy. Are you ready to collaborate?"
  },
  {
    id: "agile-engineer",
    name: "Agile Process Engineer",
    avatar: "⚙️",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/PrositionsNono/Sage-Leaders.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.Agile.png",
    specialty: "Operations & Efficiency",
    icon: Cog,
    description: "Hi! I'm Agile Process Engineer, your Operations & Efficiency expert. Expert guidance on process optimization, workflow design, and operational efficiency for rapid growth!"
  },
  {
    id: "health-wellness",
    name: "Health & Wellness Expert",
    avatar: "❤️",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/KleeKaren2.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.Health.png",
    specialty: "Holistic Well-Being",
    icon: Heart,
    description: "Hi! I'm Health & Wellness Expert, your Holistic Well-Being expert. Guidance on mental health, relationships, fitness, and general wellness. Are you ready to share your goals?"
  },
  {
    id: "security-readiness",
    name: "Security & Readiness Expert",
    avatar: "🛡️",
    videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/USMCWentlang.mp4",
    thumbnailUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/1.Security.png",
    specialty: "Security & Risk Management",
    icon: Shield,
    description: "Expert in cybersecurity, risk assessment, threat mitigation, and organizational readiness"
  },
];

export function UnifiedAIAgent({ 
  isOpen,
  onClose,
  mode = "pwa",
  currentTier = "Free",
  theme = "light",
  onThemeToggle,
  showNavigation = true,
  onUpgradeClick
}: UnifiedAIAgentProps) {
  const { navigate } = useNavigation();
  const { addActivity } = useRecentActivity();
  
  // State Management
  const [viewState, setViewState] = useState<"carousel" | "chat" | "video-expanded">("carousel"); // Start with carousel/welcome screen
  const [selectedAgent, setSelectedAgent] = useState<SMEAgent | null>(null); // No agent selected initially
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showConnectionsManager, setShowConnectionsManager] = useState(false);
  const [showRecentActivity, setShowRecentActivity] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(4); // Default to Product Owner - AI Leader (index 4)
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);
  const [showPricingPage, setShowPricingPage] = useState(false);
  
  // Container resize state
  const [containerHeight, setContainerHeight] = useState(883); // Default 883px
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartY, setResizeStartY] = useState(0);
  const [resizeStartHeight, setResizeStartHeight] = useState(883);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [recognition, setRecognition] = useState<any>(null);

  // Video drag state
  const [videoPosition, setVideoPosition] = useState({ x: 0, y: 0 });
  const [isDraggingVideo, setIsDraggingVideo] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 }); // Track initial drag position
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const expandedVideoRef = useRef<HTMLVideoElement>(null);

  // Modal drag state (desktop only)
  const [isDraggingModal, setIsDraggingModal] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [modalDragStart, setModalDragStart] = useState({ x: 0, y: 0 });
  const modalDragHandleRef = useRef<HTMLDivElement>(null);

  // Current video URL (defaults to selected agent's video, can be overridden by keyword triggers)
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>(selectedAgent?.videoUrl || "");

  // Keyword-to-Video Mapping (case-insensitive detection)
  const keywordVideoMap: Record<string, { keywords: string[], videoUrl: string }> = {
    "CFO_Finance": {
      keywords: ["CFO", "finance", "financial", "Chief Financial Officer", "USMC"],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/CWO-John2026.mp4"
    },
    "AI_Leadership": {
      keywords: [
        "KPI", "Key Performance Indicator", "Key Performance Indicators", "KPIs",
        "Investment", "Return", "Measurable", "ROI",
        "Mistakes", "Lessons Learned", "AI Leadership", "Invest", 
        "skills", "Strategic workforce", "Return on investment", "applied AI"
      ],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/AI_Literacy_GovBriefpart2.mp4"
    },
    "MIT_Transformation": {
      keywords: ["MIT", "Transformation", "Agentic", "organizational change"],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/MIT_Arnold.mp4"
    },
    "Training_Innovation": {
      keywords: ["Training", "innovation", "additive processing"],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/Process-Citrus.mp4"
    },
    "Security_Risk": {
      keywords: ["Readiness", "security", "cyber security", "cybersecurity", "responsible AI", "Bias", "Vendor lock-in", "loss", "risk"],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/USMCWentlang.mp4"
    },
    "Agile_Process": {
      keywords: ["Agile", "Scrum", "Agile Processes", "User stories", "profit", "rapid delivery", "Lean", "teams"],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/AgileMike.mp4"
    },
    "Legal_Compliance": {
      keywords: ["legal", "compliance", "government", "bureaucracy", "modular", "presentations", "Capabilities", "field tested", "cognitive", "recognizance"],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/PrositionsNono/AI-Joint-Leaders.mp4"
    },
    "Data_Science": {
      keywords: ["Data Validation", "Data analysis", "Data scientist", "data analytics", "metrics", "ML", "machine learning"],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/Validation1.mp4"
    },
    "Health_Wellness": {
      keywords: ["Human health", "Wellness", "fitness", "coaching", "goals", "gut", "iron man", "ironman", "personal training", "cycle", "running", "swimming", "citizenship", "deported"],
      videoUrl: "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/KleeKaren2.mp4"
    }
  };

  // Function to detect keywords and return matching video URL
  const detectKeywordVideo = (text: string): string | null => {
    const lowerText = text.toLowerCase();
    
    for (const category in keywordVideoMap) {
      const { keywords, videoUrl } = keywordVideoMap[category];
      
      // Check if any keyword matches (case-insensitive)
      for (const keyword of keywords) {
        if (lowerText.includes(keyword.toLowerCase())) {
          return videoUrl;
        }
      }
    }
    
    return null;
  };

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'en-US';

        recognitionInstance.onresult = (event: any) => {
          let finalTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            }
          }
          if (finalTranscript) {
            setInputValue(prev => prev + finalTranscript);
          }
        };

        setRecognition(recognitionInstance);
      }
    }
  }, []);

  // Sync expanded video when expanding
  useEffect(() => {
    if (viewState === "video-expanded" && expandedVideoRef.current && videoRef.current) {
      console.log('Video expanded - syncing playback');
      
      // Sync current time
      const currentTime = videoRef.current.currentTime || 0;
      expandedVideoRef.current.currentTime = currentTime;
      
      // Auto-play
      expandedVideoRef.current.play().catch(err => {
        console.warn('Auto-play failed on expand:', err);
      });
    }
  }, [viewState]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize default AI Engineer agent on open
  useEffect(() => {
    if (isOpen && selectedAgent && messages.length === 0) {
      // Video will auto-play via onLoadedMetadata callback when it's ready
      // This prevents race conditions that were causing enlargement/crashes

      // Add welcome message
      const welcomeMessage: Message = {
        role: "assistant",
        content: selectedAgent.description.startsWith("Hi!") 
          ? selectedAgent.description 
          : `Hi! I'm ${selectedAgent.name}, your ${selectedAgent.specialty} expert. ${selectedAgent.description}`,
        id: `msg_${Date.now()}`,
        timestamp: Date.now(),
      };
      setMessages([welcomeMessage]);

      // Track video view
      trackVideoView(selectedAgent.videoUrl, selectedAgent.name);
    }
  }, [isOpen, selectedAgent]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsTransitioning(false);
      setViewState("carousel");
      setSelectedAgent(null); // Reset to no agent selected
      setMessages([]);
      setInputValue("");
      setIsVideoExpanded(false);
      setShowMenu(false);
      setCarouselIndex(4); // Reset to center on Product Owner - AI Leader (index 4)
      setVideoPosition({ x: 0, y: 0 }); // Reset video position
    }
  }, [isOpen]);

  // Show upgrade modal once every 24 hours for non-subscribers
  useEffect(() => {
    if (!isOpen || currentTier !== 'Free') return;

    const lastShownKey = 'ora_upgrade_modal_last_shown_unified';
    const lastShown = localStorage.getItem(lastShownKey);
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    // Check if 24 hours have passed since last shown
    if (!lastShown || (now - parseInt(lastShown)) >= twentyFourHours) {
      // Show modal after a 3-second delay
      const timer = setTimeout(() => {
        onUpgradeClick?.();
        localStorage.setItem(lastShownKey, now.toString());
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, currentTier]);

  // Mobile viewport rule: Fit agent in viewport with max 883px height on mobile
  // Unless user explicitly resizes/expands the modal
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      const isMobile = window.innerWidth <= 640; // Tailwind md breakpoint
      const viewportHeight = window.innerHeight;
      
      if (isMobile) {
        // On mobile: constrain to viewport with 883px max and 16px padding (safe area)
        // This allows the modal to fit within the visible viewport
        const padding = 16;
        const maxMobileHeight = Math.min(883, viewportHeight - padding);
        setContainerHeight(maxMobileHeight);
      }
      // Desktop: keep user-resized height preference
    };

    // Set initial height on mount
    handleResize();

    // Re-check on window resize
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // Handle SME selection
  const handleSelectAgent = (agent: SMEAgent) => {
    setSelectedAgent(agent);
    setCurrentVideoUrl(agent.videoUrl); // Initialize current video URL
    setViewState("chat");
    setIsVideoExpanded(false); // Ensure video stays tiny (not expanded)
    setVideoPosition({ x: 0, y: 0 }); // Reset video to default position
    
    // Video will auto-play via onLoadedMetadata callback when it's ready
    // This prevents the race condition that was causing crashes

    // Add welcome message
    const welcomeMessage: Message = {
      role: "assistant",
      content: agent.description.startsWith("Hi!") 
        ? agent.description 
        : `Hi! I'm ${agent.name}, your ${agent.specialty} expert. ${agent.description}`,
      id: `msg_${Date.now()}`,
      timestamp: Date.now(),
    };
    setMessages([welcomeMessage]);

    // Track video view
    trackVideoView(agent.videoUrl, agent.name);
  };

  // Handle sending message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || !selectedAgent) return;

    const userMessage: Message = {
      role: "user",
      content: inputValue,
      id: `msg_${Date.now()}`,
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, userMessage]);
    
    // Detect keywords and switch video if match found
    const matchedVideoUrl = detectKeywordVideo(inputValue);
    if (matchedVideoUrl && matchedVideoUrl !== currentVideoUrl) {
      setCurrentVideoUrl(matchedVideoUrl);
      
      // Auto-play the new video with sound
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.src = matchedVideoUrl;
          videoRef.current.muted = false;
          videoRef.current.play().catch(err => {
            console.log("Video autoplay prevented:", err);
          });
        }
      }, 100);
      
      // Track the new video view
      trackVideoView(matchedVideoUrl, `Keyword-triggered: ${inputValue}`);
    }
    
    // Check for Knowledge Management keywords
    const lowerInput = inputValue.toLowerCase();
    const isKnowledgeManagement = 
      lowerInput.includes("knowledge management") || 
      lowerInput.includes("usmc knowledge management");
    
    setInputValue("");
    setIsTyping(true);

    // Generate AI response using knowledge base
    setTimeout(() => {
      let responseContent: string;
      let relatedLink: string | null = null;
      
      // Short intro phrases (3-4 words max)
      const intros = [
        "Good question. ",
        "Let's see. ",
        "Here's the thing. ",
        "That's smart. ",
        "Exactly right. ",
        "Hold on. ",
        "Think about this. ",
        "Key insight here. ",
      ];
      const randomIntro = intros[Math.floor(Math.random() * intros.length)];
      
      if (isKnowledgeManagement) {
        // Special response for Knowledge Management with clickable links
        responseContent = `${randomIntro}

📄 NAV Knowledge Management Guide:
https://www.secnav.navy.mil/doni/Directives/05000%20General%20Management%20Security%20and%20Safety%20Services/05-200%20Management%20Program%20and%20Techniques%20Services/5239.2.pdf

📚 Harvard Leadership Resources:
https://www.bok.hbs.edu/Pages``   


/browse.aspx?HBSTopic=Leadership`;
        relatedLink = "https://www.secnav.navy.mil/doni/Directives/05000%20General%20Management%20Security%20and%20Safety%20Services/05-200%20Management%20Program%20and%20Techniques%20Services/5239.2.pdf";
      } else {
        // Search knowledge base for relevant content
        const baseContent = searchKnowledgeBase(userMessage.content, selectedAgent);
        responseContent = randomIntro + baseContent;
        
        // Check for common topics that have related links
        if (lowerInput.includes("raci") || lowerInput.includes("responsibility matrix")) {
          relatedLink = "https://www.smartsheet.com/content/raci-templates-excel";
        } else if (lowerInput.includes("agile") || lowerInput.includes("scrum")) {
          relatedLink = "https://www.scrum.org/resources/what-is-scrum";
        } else if (lowerInput.includes("ai literacy") || lowerInput.includes("artificial intelligence")) {
          relatedLink = "https://www.whitehouse.gov/ai/";
        } else if (lowerInput.includes("kpi") || lowerInput.includes("key performance")) {
          relatedLink = "https://www.cio.gov/handbook/it-performance/";
        } else if (lowerInput.includes("open government") || lowerInput.includes("evidence-based policymaking")) {
          relatedLink = "https://www.whitehouse.gov/wp-content/uploads/2025/01/M-25-05-Phase-2-Implementation-of-the-Foundations-for-Evidence-Based-Policymaking-Act-of-2018-Open-Government-Data-Access-and-Management-Guidance.pdf";
        }
      }
      
      // Apply 256 character limit
      const MAX_LENGTH = 256;
      let finalContent = responseContent;
      
      if (responseContent.length > MAX_LENGTH) {
        finalContent = responseContent.substring(0, MAX_LENGTH).trim() + "...";
      }
      
      // Append link if available with custom text for RACI
      if (relatedLink && !finalContent.includes("http")) {
        const isRACILink = relatedLink.includes("smartsheet.com/content/raci-templates");
        const linkText = isRACILink ? "Get RACI Template now" : "Learn more";
        finalContent += `\n\n${linkText}: ${relatedLink}`;
      }
      
      const assistantMessage: Message = {
        role: "assistant",
        content: finalContent,
        id: `msg_${Date.now() + 1}`,
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1500);
  };

  // Handle voice input
  const handleVoiceInput = () => {
    if (!recognition) {
      alert("Voice recognition not supported in this browser");
      return;
    }

    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
    } else {
      recognition.start();
      setIsRecording(true);
    }
  };

  // Handle video expansion
  const handleVideoExpand = () => {
    console.log('handleVideoExpand called, current state:', { isVideoExpanded, viewState });
    
    try {
      if (isVideoExpanded) {
        // Collapse video
        console.log('Collapsing video...');
        setIsVideoExpanded(false);
        setViewState("chat");
        
        // Resume small video playback
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.log('Resume small video failed:', err);
          });
        }
      } else {
        // Expand video
        console.log('Expanding video...');
        
        // Pause small video immediately
        if (videoRef.current) {
          videoRef.current.pause();
          const currentTime = videoRef.current.currentTime || 0;
          console.log('Small video paused at:', currentTime);
          
          // Store the time to sync after expanded video loads
          // Set state to show expanded view
          setIsVideoExpanded(true);
          setViewState("video-expanded");
          
          // Schedule sync after a brief delay to allow expanded video to render
          setTimeout(() => {
            if (expandedVideoRef.current) {
              expandedVideoRef.current.currentTime = currentTime;
              console.log('Expanded video time synced to:', currentTime);
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error in handleVideoExpand:', error);
    }
  };

  // Handle video minimize (from expanded to chat view)
  const handleVideoMinimize = () => {
    console.log('handleVideoMinimize called');
    
    try {
      // Simply collapse the expanded video view
      setIsVideoExpanded(false);
      setViewState("chat");
      
      // Don't try to manipulate playback - let the small video resume naturally
      // The video element will maintain its playback state automatically
    } catch (error) {
      console.error('Error in handleVideoMinimize:', error);
      // Fallback: just change state
      setIsVideoExpanded(false);
      setViewState("chat");
    }
  };

  // Carousel navigation
  const handleCarouselPrev = () => {
    setCarouselIndex((prev) => (prev === 0 ? Math.max(0, SME_AGENTS.length - 4) : prev - 1));
  };

  const handleCarouselNext = () => {
    setCarouselIndex((prev) => (prev >= SME_AGENTS.length - 4 ? 0 : prev + 1));
  };

  // Navigate to Analytics
  const handleOpenAnalytics = () => {
    onClose();
    navigate('/analytics');
  };

  // Handle thumbnail click to select agent (not expand)
  const handleThumbnailClick = (agent: SMEAgent, e: React.MouseEvent) => {
    e.stopPropagation();
    // Same as clicking SELECT button - this just selects the agent
    handleSelectAgent(agent);
  };

  // Video drag handlers - DRAG TO EXPAND functionality
  const handleVideoMouseDown = (e: React.MouseEvent) => {
    if (!videoContainerRef.current) return;
    
    // Reset drag movement flag
    setHasMovedDuringDrag(false);
    
    const rect = videoContainerRef.current.getBoundingClientRect();
    setDragStartPos({
      x: e.clientX,
      y: e.clientY
    });
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDraggingVideo(true);
  };

  const handleVideoMouseMove = (e: MouseEvent) => {
    if (!isDraggingVideo) return;
    
    // Allow smooth repositioning - no drag-to-expand threshold
    let newX = e.clientX - dragOffset.x;
    let newY = e.clientY - dragOffset.y;
    
    // Video dimensions - 9:16 PORTRAIT ratio, larger size (width: 120px, height: 213px)
    const videoWidth = 120;
    const videoHeight = 213;
    const padding = 10;
    
    // Constrain to viewport bounds with 10px padding
    const maxX = window.innerWidth - videoWidth - padding;
    const maxY = window.innerHeight - videoHeight - padding;
    
    newX = Math.max(padding, Math.min(newX, maxX));
    newY = Math.max(padding, Math.min(newY, maxY));
    
    // Check if video has escaped container bounds
    if (Math.abs(newX) > 50 || Math.abs(newY - 64) > 200) {
      setIsVideoEscaped(true);
    }
    
    // Mark that we've moved during drag - prevents expand on mouseup
    if (newX !== videoPosition.x || newY !== videoPosition.y) {
      setHasMovedDuringDrag(true);
    }
    
    setVideoPosition({ x: newX, y: newY });
  };

  const handleVideoMouseUp = () => {
    setIsDraggingVideo(false);
  };

  // Track if we've moved the video during drag
  const [hasMovedDuringDrag, setHasMovedDuringDrag] = useState(false);

  // Add/remove mouse move and up listeners for video dragging
  useEffect(() => {
    if (isDraggingVideo) {
      document.addEventListener('mousemove', handleVideoMouseMove);
      document.addEventListener('mouseup', handleVideoMouseUp);
    } else {
      document.removeEventListener('mousemove', handleVideoMouseMove);
      document.removeEventListener('mouseup', handleVideoMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleVideoMouseMove);
      document.removeEventListener('mouseup', handleVideoMouseUp);
    };
  }, [isDraggingVideo, dragOffset]);

  // Modal drag handlers (desktop only - screen width > 640px)
  const handleModalMouseDown = (e: React.MouseEvent) => {
    // Only enable drag on desktop (screen width > 640px)
    if (window.innerWidth <= 640) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🎯 Modal drag started - mouseDown detected');
    
    setIsDraggingModal(true);
    setModalDragStart({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y
    });
  };

  const handleModalMouseMove = (e: MouseEvent) => {
    if (!isDraggingModal || window.innerWidth <= 640) return;
    
    const newX = e.clientX - modalDragStart.x;
    const newY = e.clientY - modalDragStart.y;
    
    setModalPosition({ x: newX, y: newY });
  };

  const handleModalMouseUp = () => {
    setIsDraggingModal(false);
  };

  // Add/remove mouse move and up listeners for modal dragging
  useEffect(() => {
    if (isDraggingModal) {
      document.addEventListener('mousemove', handleModalMouseMove);
      document.addEventListener('mouseup', handleModalMouseUp);
    } else {
      document.removeEventListener('mousemove', handleModalMouseMove);
      document.removeEventListener('mouseup', handleModalMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleModalMouseMove);
      document.removeEventListener('mouseup', handleModalMouseUp);
    };
  }, [isDraggingModal, modalDragStart]);

  // Reset modal position when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setModalPosition({ x: 0, y: 0 });
      setIsDraggingModal(false);
    }
  }, [isOpen]);

  // Container resize handlers
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStartY(e.clientY);
    setResizeStartHeight(containerHeight);
  };

  const handleResizeMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;
    
    const deltaY = e.clientY - resizeStartY;
    const newHeight = resizeStartHeight + deltaY;
    
    // Constrain height: minimum 600px, maximum 95vh
    const minHeight = 600;
    const maxHeight = window.innerHeight * 0.95;
    
    setContainerHeight(Math.max(minHeight, Math.min(newHeight, maxHeight)));
  };

  const handleResizeMouseUp = () => {
    setIsResizing(false);
  };

  // Add/remove resize listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, resizeStartY, resizeStartHeight]);

  // Carousel View - Based on Mike's wireframe design
  const renderCarouselView = () => {
    const agents = SME_AGENTS;
    
    // RULE: Always show exactly 5 agents (2 left + center + 2 right)
    const visibleAgents = [];
    for (let i = -2; i <= 2; i++) {
      const index = carouselIndex + i;
      // Use modulo to wrap around at edges, ensuring 5 are always visible
      const wrappedIndex = ((index % agents.length) + agents.length) % agents.length;
      visibleAgents.push({ agent: agents[wrappedIndex], offset: i, actualIndex: wrappedIndex });
    }
    
    return (
      <div className="h-full flex flex-col bg-white">
        {/* Upper section - Welcome Screen */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Welcome text - 15px below top navigation hairline (shifted up 3px) */}
          <div className="text-center pt-[15px]">
            <p className="text-gray-800 text-sm font-normal -mt-[3px]">Welcome</p>
          </div>
          
          {/* ORA and tagline - centered in remaining space */}
          <div className="flex-1 flex flex-col justify-center text-center px-4 min-h-0">
            {/* O R A - Closer spacing and bolder */}
            <h1 className="text-7xl font-black text-black mb-[10px] tracking-[0.15em]">ORA</h1>
            
            {/* Observe Respond Act - 10px below ORA with letter spacing like image */}
            <p className="text-sm text-gray-600 font-normal tracking-[0.2em]">
              Observe Respond Act
            </p>
          </div>
        </div>

        {/* Lower section - Carousel Section with more space for agents */}
        <div className="bg-gray-200 border-t border-gray-300 flex flex-col justify-start pt-3 px-4 pb-4 shadow-lg shadow-purple-900/30" style={{ minHeight: '240px' }}>
          {/* Agent Thumbnails Row - 5 visible (2 left + center + 2 right) */}
          <div className="flex-1 flex items-start justify-center gap-1 mb-2 overflow-visible" style={{ marginTop: '-4px' }}>
            {visibleAgents.map(({ agent, offset, actualIndex }) => {
              const isCenter = offset === 0;
              
              return (
                <div
                  key={agent.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCenter) {
                      // If center thumbnail is clicked, select the agent
                      console.log('Center thumbnail clicked. Selecting agent:', agent.name);
                      handleSelectAgent(agent);
                    } else {
                      // If non-center thumbnail is clicked, just center it (no selection)
                      console.log('Thumbnail clicked. Setting carousel index to:', actualIndex);
                      setCarouselIndex(actualIndex);
                    }
                  }}
                  className="flex flex-col items-center cursor-pointer group flex-shrink-0"
                >
                  {/* Thumbnail Box - 9:16 Portrait Ratio - Center is 12% larger - Smaller for mobile */}
                  <div
                    className={`
                      border border-gray-300 bg-white rounded-lg
                      flex items-center justify-center
                      transition-all group-hover:scale-105 group-hover:shadow-md
                      shadow-sm overflow-hidden relative
                      ${isCenter ? 'w-[68px] h-[121px] md:w-[90px] md:h-[159px] ring-4 ring-blue-600' : 'w-[58px] h-[103px] md:w-20 md:h-[142px]'}
                    `}
                  >
                    {/* Show thumbnail image if available, otherwise show video preview */}
                    {agent.thumbnailUrl ? (
                      <img
                        src={agent.thumbnailUrl}
                        alt={agent.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <video
                        src={agent.videoUrl}
                        className="w-full h-full object-cover"
                        style={{ 
                          objectPosition: agent.id === 'agile-engineer' ? 'center 20%' : 'center center',
                          transform: agent.id === 'agile-engineer' ? 'scale(1.2)' : 'none'
                        }}
                        muted
                        playsInline
                        preload="metadata"
                      />
                    )}
                    
                    {/* Dark overlay for non-center thumbnails */}
                    {!isCenter && (
                      <div className="absolute inset-0 bg-black/[0.33] pointer-events-none" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Agent Name Display - Larger text above Select button */}
          <div className="text-center py-2">
            <p className="text-sm md:text-base font-semibold text-gray-900">
              {agents[carouselIndex]?.name}
            </p>
          </div>

          {/* Select Button with Side Arrows - Button moved to div line, flush right */}
          <div className="flex items-center justify-between px-4 py-2">
            {/* Left Arrow - 60% white circle with white chevron - Always enabled with wrapping */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Wrap around using modulo
                const newIndex = ((carouselIndex - 1) + agents.length) % agents.length;
                console.log('Bottom Left arrow clicked. Current:', carouselIndex, 'New:', newIndex);
                setCarouselIndex(newIndex);
              }}
              className="bg-white/60 hover:bg-white/70 p-1 rounded-full transition-all"
            >
              <ChevronLeft className="size-5 text-gray-800" strokeWidth={2.5} />
            </button>

            {/* Select Button - Gradient blue-700-to-blue-900 */}
            <button
              onClick={() => {
                const agent = agents[carouselIndex];
                if (agent) handleSelectAgent(agent);
              }}
              disabled={isTransitioning}
              className={`px-8 py-1.5 md:px-10 md:py-1.5 bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950 text-white font-bold text-xs md:text-sm rounded-lg shadow-lg hover:shadow-blue-500/50 transition-all ${
                isTransitioning ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ color: 'white' }}
            >
              {isTransitioning ? 'Loading...' : 'Select'}
            </button>

            {/* Right Arrow - 60% white circle with white chevron - Always enabled with wrapping */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Wrap around using modulo
                const newIndex = (carouselIndex + 1) % agents.length;
                console.log('Bottom Right arrow clicked. Current:', carouselIndex, 'New:', newIndex);
                setCarouselIndex(newIndex);
              }}
              className="bg-white/60 hover:bg-white/70 p-1 rounded-full transition-all"
            >
              <ChevronRight className="size-5 text-gray-800" strokeWidth={2.5} style={{ color: 'text-gray-800' }} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay Background */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Main Agent Container - ALWAYS LIGHT MODE */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
          style={{ 
            width: '100%',
            maxWidth: '444px',
            height: `${containerHeight}px`,
            maxHeight: '883px',
            transform: window.innerWidth > 640 ? `translate(${modalPosition.x}px, ${modalPosition.y}px)` : 'none',
            cursor: isDraggingModal ? 'grabbing' : 'default'
          }}
        >
          {/* Draggable Header Bar - Outside scrollable content */}
          <div 
            ref={modalDragHandleRef}
            className="bg-gray-150 z-20 py-2 px-3 border-b border-gray-300 cursor-grab active:cursor-grabbing select-none hover:bg-gray-200 transition-colors pointer-events-auto"
            style={{ userSelect: 'none', touchAction: 'none', backgroundColor: '#d4d5d7' }}
            onMouseDown={handleModalMouseDown}
            title="Drag to move the agent"
          >
            <div className="flex items-center justify-center gap-2 pointer-events-none">
              <div className="w-8 h-1 bg-gray-400 rounded-full"></div>
            </div>
          </div>

          {/* Header */}
          <div className="relative flex items-center justify-between px-4 py-4 min-h-[64px] border-b border-gray-200 bg-gray-200 shadow-lg shadow-gray-900/30">
            {/* Title - Allow 2 lines with proper wrapping */}
            <h2 className="text-sm font-bold !text-black leading-tight max-w-[60%] pr-2" style={{ lineHeight: '1.2' }}>
              {viewState === "carousel" ? "ORA" : selectedAgent?.name}
            </h2>
            
            {/* Controls - Brain, Triangle, Clear, Dashboard, X - all aligned to center */}
            {viewState === "chat" && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Brain - Change Agent */}
                <button
                  onClick={() => setViewState("carousel")}
                  className="p-1.5 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
                  title="Change Agent"
                >
                  <Brain className="size-4 !text-black fill-yellow-400" />
                </button>
                
                {/* Triangle - Report Issue */}
                <button
                  onClick={() => {
                    setIsReportIssueOpen(true);
                  }}
                  className="p-1.5 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
                  title="Report Issue"
                >
                  <svg className="w-[17.6px] h-[17.6px] !text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </button>
                
                {/* Close Video */}
                <button
                  onClick={() => {
                    // Clear chat messages
                    setMessages([]);
                    
                    // Helper function to get random different video
                    const getRandomDifferentVideo = (): string => {
                      let randomIndex = Math.floor(Math.random() * SME_AGENTS.length);
                      let randomAgent = SME_AGENTS[randomIndex];
                      
                      // Ensure it's not the same video as currently playing
                      if (randomAgent && randomAgent.videoUrl !== currentVideoUrl) {
                        return randomAgent.videoUrl;
                      } else {
                        // If randomly selected same video, use next agent
                        const altIndex = (randomIndex + 1) % SME_AGENTS.length;
                        const altAgent = SME_AGENTS[altIndex];
                        return altAgent?.videoUrl || "";
                      }
                    };
                    
                    // Always switch to a random different video
                    const newVideoUrl = getRandomDifferentVideo();
                    if (newVideoUrl) {
                      setCurrentVideoUrl(newVideoUrl);
                      // Also reset video refs if they exist
                      if (videoRef.current) {
                        videoRef.current.pause();
                        videoRef.current.currentTime = 0;
                      }
                      if (expandedVideoRef.current) {
                        expandedVideoRef.current.pause();
                        expandedVideoRef.current.currentTime = 0;
                      }
                    }
                  }}
                  className="p-1.5 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
                  title="Clear Chat and Switch Video"
                >
                  <Trash2 className="size-4 fill-gray-300 text-gray-400" />
                </button>
                
                {/* Dashboard */}
                <button
                  onClick={handleOpenAnalytics}
                  className="p-1.5 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
                  title="View Analytics"
                >
                  <BarChart3 className="size-4 !text-black" />
                </button>
                
                {/* X button */}
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
                >
                  <X className="size-4 !text-black" />
                </button>
              </div>
            )}
            
            {/* X button for carousel view */}
            {viewState === "carousel" && (
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="size-4 !text-black" />
              </button>
            )}
          </div>

          {/* CAROUSEL VIEW */}
          {viewState === "carousel" && (
            renderCarouselView()
          )}

          {/* CHAT VIEW */}
          {viewState === "chat" && selectedAgent && (
            <div className="flex-1 flex flex-col relative min-h-0">
              {/* Video Corner - Always contained within modal */}
              <div 
                ref={videoContainerRef}
                className={`absolute ${isVideoExpanded ? 'z-0 opacity-0 pointer-events-none' : 'z-[9999]'} ${isDraggingVideo ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={{
                  top: '10px',
                  right: '8px',
                  width: '126px',
                  height: '224px',
                  pointerEvents: isVideoExpanded ? 'none' : 'auto',
                  transition: isVideoExpanded ? 'none' : 'opacity 0.2s ease-out',
                }}
                onMouseDown={handleVideoMouseDown}
              >
                <div 
                  className="rounded-xl overflow-hidden shadow-lg border border-blue-500 hover:border-blue-600 transition-colors select-none relative cursor-pointer"
                  style={{
                    width: '100%',
                    height: '100%',
                    display: isVideoExpanded ? 'none' : 'block',
                    visibility: isVideoExpanded ? 'hidden' : 'visible',
                    opacity: isVideoExpanded ? 0 : 1,
                    transition: 'opacity 0.3s ease-out, visibility 0.3s ease-out',
                  }}
                  onClick={(e) => {
                    // Only expand on click if we didn't drag the video
                    if (!isDraggingVideo && !hasMovedDuringDrag) {
                      e.stopPropagation();
                      handleVideoExpand();
                    }
                  }}
                >
                  {selectedAgent && (
                    <video
                      key={`video-${selectedAgent.id}-${currentVideoUrl}`}
                      ref={videoRef}
                      src={currentVideoUrl || selectedAgent.videoUrl}
                      className="w-full h-full object-cover pointer-events-none"
                      playsInline
                      preload="metadata"
                      crossOrigin="anonymous"
                      style={{
                        display: 'block',
                        visibility: 'visible',
                        opacity: 1,
                      }}
                      onError={(e) => {
                        console.error('Small video error:', e);
                        console.log('Video src:', currentVideoUrl || selectedAgent.videoUrl);
                      }}
                      onLoadedMetadata={() => {
                        console.log('Small video metadata loaded - unmuting for audio');
                        if (videoRef.current) {
                          videoRef.current.muted = false; // Enable audio
                          videoRef.current.play().catch(err => {
                            console.warn('Small video autoplay failed:', err);
                          });
                        }
                      }}
                    />
                  )}

                </div>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 pt-[24px]">
                {messages.map((message) => {
                  // Function to render message content with clickable links
                  const renderMessageContent = (content: string) => {
                    // Split content by URLs
                    const urlRegex = /(https?:\/\/[^\s]+)/g;
                    const parts = content.split(urlRegex);
                    
                    return parts.map((part, index) => {
                      if (part.match(urlRegex)) {
                        return (
                          <a
                            key={index}
                            href={part}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-800 underline hover:text-blue-600 break-all"
                          >
                            {part}
                          </a>
                        );
                      }
                      return <span key={index}>{part}</span>;
                    });
                  };

                  return (
                    <div
                      key={message.id}
                      className={`mb-4 ${message.role === "user" ? "text-right" : "text-left"}`}
                    >
                      <div
                        className={`inline-block max-w-[calc(80%-12px)] p-3 rounded-2xl ${
                          message.role === "user"
                            ? "bg-purple-200 text-black"
                            : "bg-gray-200 text-black"
                        }`}
                        style={message.role === "assistant" ? { marginLeft: '-7px', marginTop: '-6px' } : {}}
                      >
                        <p className="text-[8px] leading-[1.1] whitespace-pre-wrap text-black">
                          {renderMessageContent(message.content)}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {isTyping && (
                  <div className="mb-4 text-left">
                    <div className="inline-block bg-gray-100 p-3 rounded-2xl">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Input Area */}
              <div className="px-4 py-3 border-t border-gray-200 shadow-lg shadow-purple-900/30" style={{ backgroundColor: '#d4d5d7' }}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                      placeholder="Ask?"
                      className="w-full px-4 py-3 pr-12 bg-gray-100 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-black placeholder-gray-500"
                    />
                    <button
                      onClick={handleVoiceInput}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${
                        isRecording ? "bg-red-500 text-white" : "hover:bg-gray-200 text-white"
                      }`}
                    >
                      <Mic className="size-5" />
                    </button>
                  </div>
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-3 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <Menu className="size-6 text-white" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* VIDEO EXPANDED VIEW - Full screen within modal */}
          {viewState === "video-expanded" && selectedAgent && (
            <div 
              className="absolute inset-0 z-50 bg-black flex flex-col items-center justify-center overflow-hidden"
            >
              <div 
                className="relative w-full h-full flex items-center justify-center bg-black"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              >
                <video
                  key={`expanded-video-${selectedAgent.id}-${currentVideoUrl}`}
                  ref={expandedVideoRef}
                  src={currentVideoUrl || selectedAgent.videoUrl}
                  controls
                  playsInline
                  preload="auto"
                  crossOrigin="anonymous"
                  controlsList="nodownload"
                  className="w-full h-full"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    backgroundColor: '#000',
                  }}
                  onError={(e) => {
                    console.error('Expanded video error:', e);
                    console.log('Video src:', currentVideoUrl || selectedAgent.videoUrl);
                  }}
                  onCanPlay={() => {
                    console.log('Expanded video can play');
                  }}
                  onLoadedData={() => {
                    console.log('Expanded video loaded data');
                  }}
                  onLoadedMetadata={() => {
                    console.log('Expanded video loaded metadata - attempting autoplay with SOUND');
                    if (expandedVideoRef.current) {
                      expandedVideoRef.current.muted = false; // UNMUTE for sound
                      expandedVideoRef.current.play().catch(err => {
                        console.warn('Auto-play failed:', err);
                      });
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleVideoMinimize();
                  }}
                  className="absolute top-4 right-4 p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors z-50"
                  title="Minimize video"
                >
                  <Minimize2 className="size-6 text-white" />
                </button>
              </div>
            </div>
          )}
          
          {/* Resize Handle - Bottom of container */}
          <div 
            className="h-2 bg-gray-300 hover:bg-blue-500 cursor-ns-resize transition-colors flex items-center justify-center group relative"
            onMouseDown={handleResizeMouseDown}
            title="Drag to resize"
          >
            {/* Visual indicator - 3 horizontal lines */}
            <div className="absolute inset-x-0 flex items-center justify-center pointer-events-none">
              <div className="flex flex-col gap-[2px]">
                <div className="w-8 h-[2px] bg-gray-500 group-hover:bg-white transition-colors rounded-full"></div>
                <div className="w-8 h-[2px] bg-gray-500 group-hover:bg-white transition-colors rounded-full"></div>
                <div className="w-8 h-[2px] bg-gray-500 group-hover:bg-white transition-colors rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Side Menu Drawer */}
      {showMenu && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <div 
            className="flex-1 bg-black/50"
            onClick={() => setShowMenu(false)}
          />
          <div className="w-80 bg-white shadow-2xl overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-black">Menu</h3>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="size-5 text-white" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-2">
              <button
                onClick={() => setViewState("carousel")}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <Brain className="size-5 text-purple-600 fill-yellow-400" />
                <span className="text-sm font-medium text-black">Change Agents</span>
              </button>

              <button
                onClick={() => {
                  setShowConnectionsManager(true);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <Users className="size-5 text-purple-600" />
                <span className="text-sm font-medium text-black">Agent Connections</span>
              </button>

              <button
                onClick={() => {
                  setIsReportIssueOpen(true);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <Shield className="size-5 text-purple-600" />
                <span className="text-sm font-medium text-black">Report Issue</span>
              </button>

              <button
                onClick={() => {
                  onClose();
                  navigate('/configuration');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <Settings className="size-5 text-purple-600" />
                <span className="text-sm font-medium text-black">Configuration</span>
              </button>

              <button
                onClick={() => {
                  setShowRecentActivity(true);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <BarChart3 className="size-5 text-purple-600" />
                <span className="text-sm font-medium text-black">Recent Activity</span>
              </button>

              <button
                onClick={handleOpenAnalytics}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <TrendingUp className="size-5 text-purple-600" />
                <span className="text-sm font-medium text-black">AI Analytics Dashboard</span>
              </button>

              {/* Company Section Divider */}
              <div className="my-4 border-t border-gray-200"></div>
              <div className="px-4 py-2">
                <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide">Company</h4>
              </div>

              {/* Company Links */}
              <button
                onClick={() => {
                  onClose();
                  navigate('/about');
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <span className="text-sm font-medium text-black">About</span>
              </button>

              <button
                onClick={() => {
                  onClose();
                  navigate('/mission');
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
              >
                <span className="text-sm font-medium text-black">Mission</span>
              </button>

              <button
                onClick={() => {
                  window.open('https://agent.myora.now', '_blank');
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-100 rounded-lg text-left transition-colors"
                title="Open FREE AI Leadership Training"
              >
                <BookOpen className="size-5 text-purple-600" />
                <span className="text-sm font-medium text-black">FREE AI Leadership Training</span>
              </button>

              <button
                onClick={() => {
                  setShowPricingPage(true);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 rounded-lg text-left transition-colors"
                title="View pricing options"
              >
                <span className="text-sm font-medium text-black">Pricing Options</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agent Connections Manager Modal */}
      {showConnectionsManager && (
        <AgentConnectionsManager
          isOpen={showConnectionsManager}
          onClose={() => setShowConnectionsManager(false)}
          currentTier={currentTier}
          onUpgradeClick={() => navigate('/pricing')}
        />
      )}

      {/* Recent Activity Modal */}
      {showRecentActivity && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-black">Recent Activity</h3>
              <button
                onClick={() => setShowRecentActivity(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="size-5 text-gray-600" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
              <RecentActivity isOpen={showRecentActivity} onClose={() => setShowRecentActivity(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {isReportIssueOpen && (
        <ReportIssueModal
          isOpen={isReportIssueOpen}
          onClose={() => setIsReportIssueOpen(false)}
          subscriberEmail=""
          onSaveReport={(reportData) => {
            // Add saved report to Recent Activity
            addActivity({
              observation: reportData.observation,
              additionalContext: reportData.recommendation ? `Action: ${reportData.actionType}\n\nRecommendation: ${reportData.recommendation}` : undefined,
              topic: reportData.topic,
              actionType: reportData.actionType,
              sentiment: reportData.sentiment,
            });
          }}
        />
      )}

      {/* Pricing Page */}
      {showPricingPage && (
        <PricingPage
          onClose={() => setShowPricingPage(false)}
        />
      )}
    </>
  );
}